// Entry point for the play-index bundle. Dispatches by page based on
// which container IDs are present:
//   - #filters    → play-index (events / shotmap / CSV)
//   - #lb-filters → leaderboard (top_skaters_on_ice)
//   - #g-filters  → goalies (top_goalies)
// Single bundle loads on all three so the cron-build-deploy step
// doesn't have to per-page slice it.

import { buildCsv, triggerDownload } from "./csv";
import { initDraft } from "./draft";
import { initExpansion } from "./expansion";
import { initExpansionTeam } from "./team";
import { renderEventsTable, renderShotmap } from "./events-table";
import { hydrateFromUrl, readFilters } from "./form";
import { initGoalies } from "./goalies";
import { initLeaderboard } from "./leaderboard";
import { runEventsQuery } from "./query";
import { buildDotsSvg, buildHexbinSvg, type ShotmapRow } from "./shotmap";

type ViewMode = "dots" | "hexbin" | "table";

// Limits picked to match the original server defaults: /events paginates
// at 100 rows; /shotmap pulls up to 10k so a season-wide query still
// renders all points.
const TABLE_PAGE_SIZE = 100;
const SHOTMAP_LIMIT = 10000;

const VIEW_BY_ENDPOINT: Record<string, ViewMode> = {
  "/shotmap?style=dots": "dots",
  "/shotmap?style=hexbin": "hexbin",
  "/events": "table",
};

interface UiState {
  view: ViewMode;
  offset: number;
}

const state: UiState = { view: "dots", offset: 0 };

function getElements(): {
  form: HTMLFormElement;
  results: HTMLElement;
  csvLink: HTMLAnchorElement;
  viewBtns: HTMLButtonElement[];
} | null {
  const form = document.getElementById("filters") as HTMLFormElement | null;
  const results = document.getElementById("results");
  const csvLink = document.getElementById("csv-link") as HTMLAnchorElement | null;
  if (!form || !results || !csvLink) return null;
  const viewBtns = Array.from(
    document.querySelectorAll<HTMLButtonElement>(".view-btn"),
  );
  return { form, results, csvLink, viewBtns };
}

async function render(): Promise<void> {
  const els = getElements();
  if (!els) return;
  const { form, results } = els;

  const filters = readFilters(form);
  if (state.view === "table") {
    filters.limit = TABLE_PAGE_SIZE;
    filters.offset = state.offset;
  } else {
    filters.limit = SHOTMAP_LIMIT;
    filters.offset = 0;
  }

  results.innerHTML = '<div class="empty" style="padding:1.5rem;text-align:center;">Loading…</div>';

  try {
    const { rows, total } = await runEventsQuery(filters);
    if (state.view === "table") {
      const hasXg = rows.some((r) => r.xg !== null);
      results.innerHTML = renderEventsTable({
        rows,
        total,
        offset: filters.offset,
        limit: filters.limit,
        hasXg,
      });
      wirePagerButtons(results);
    } else {
      const shotmapRows: ShotmapRow[] = rows.map((r) => ({
        x_loc: r.x_loc,
        y_loc: r.y_loc,
        zone: r.zone,
        xg: r.xg,
        is_goal: r.is_goal,
        player_name: r.player_name,
        team_code: r.team_code,
        game_id: r.game_id,
        pressure_sequence_tag: r.pressure_sequence_tag,
      }));
      let svg: string;
      let plotted = total;
      if (state.view === "dots") {
        const out = buildDotsSvg(shotmapRows);
        svg = out.svg;
        plotted = out.plotted;
      } else {
        svg = buildHexbinSvg(shotmapRows);
      }
      const goals = rows.reduce((acc, r) => acc + (r.is_goal ? 1 : 0), 0);
      const xgTotal = rows.reduce((acc, r) => acc + (r.xg ?? 0), 0);
      const hasXg = rows.some((r) => r.xg !== null);
      results.innerHTML = renderShotmap({
        svg,
        total,
        goals,
        xgTotal,
        hasXg,
        style: state.view,
        plotted,
      });
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    results.innerHTML = `<p class="empty">Query failed: ${escapeHtml(msg)}</p>`;
    console.error("[play-index] query failed:", err);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function wirePagerButtons(container: HTMLElement): void {
  for (const btn of container.querySelectorAll<HTMLButtonElement>(
    ".mp-pager button",
  )) {
    btn.addEventListener("click", () => {
      const dir = btn.dataset["action"];
      if (dir === "prev") {
        state.offset = Math.max(0, state.offset - TABLE_PAGE_SIZE);
      } else if (dir === "next") {
        state.offset += TABLE_PAGE_SIZE;
      } else {
        return;
      }
      void render();
    });
  }
}

function debounce<T extends (...args: never[]) => void>(fn: T, ms: number): T {
  let h: ReturnType<typeof setTimeout> | null = null;
  return ((...args: never[]) => {
    if (h !== null) clearTimeout(h);
    h = setTimeout(() => {
      h = null;
      fn(...args);
    }, ms);
  }) as T;
}

function init(): void {
  // Dispatch by page. initLeaderboard / initGoalies return true if they
  // found their containers — when they do, the play-index init below
  // bails (its #filters / #results lookup would fail anyway). Single
  // bundle, three pages.
  if (initLeaderboard()) return;
  if (initGoalies()) return;
  if (initDraft()) return;
  if (initExpansion()) return;
  if (initExpansionTeam()) return;

  const els = getElements();
  if (!els) return;
  const { form, csvLink, viewBtns } = els;

  hydrateFromUrl(form);

  // View toggle: clicking sets the view mode and re-renders. The button's
  // data-endpoint attribute identifies the view (mirrors the original
  // HTMX-driven behavior); empty data-endpoint defaults to dots.
  viewBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      viewBtns.forEach((x) => x.classList.remove("active"));
      btn.classList.add("active");
      const endpoint = btn.dataset["endpoint"] ?? "";
      state.view = VIEW_BY_ENDPOINT[endpoint] ?? "dots";
      state.offset = 0;
      void render();
    });
  });

  // Initial view comes from whichever button is .active in the markup
  // (default dots).
  const activeBtn = viewBtns.find((b) => b.classList.contains("active"));
  state.view = activeBtn
    ? (VIEW_BY_ENDPOINT[activeBtn.dataset["endpoint"] ?? ""] ?? "dots")
    : "dots";

  // Form-driven refilter. Match the original HTMX trigger
  // ("submit, change delay:150ms from:select") — submit fires immediately,
  // selects debounce 150 ms, text inputs only on submit.
  const refilter = (): void => {
    state.offset = 0;
    void render();
  };
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    refilter();
  });
  const debouncedRefilter = debounce(refilter, 150);
  form.addEventListener("change", (e) => {
    if (e.target instanceof HTMLSelectElement) debouncedRefilter();
  });

  // CSV button: build the full result set (up to 10k) and download.
  // Doesn't share state.offset — exports the whole filtered population.
  csvLink.addEventListener("click", async (e) => {
    e.preventDefault();
    const filters = readFilters(form);
    filters.limit = SHOTMAP_LIMIT;
    filters.offset = 0;
    try {
      const { rows } = await runEventsQuery(filters);
      const csv = buildCsv(rows);
      triggerDownload(`pwhl_events_${filters.event_group}.csv`, csv);
    } catch (err) {
      console.error("[play-index] CSV export failed:", err);
      alert(`CSV export failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  void render();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
